import {
  Collapse,
  Divider,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { ReactComponent as SortableColumn } from "../../../shared/assets/sort_icons/sortable-column.svg";
import { ReactComponent as SortIcon } from "../../../shared/assets/sort_icons/sort-icon.svg";
import { ReactComponent as DownloadIcon } from "../../../shared/assets/general/download.svg";
import { getDates } from "../../../shared/utils/utils";
import store, { RootState } from "../../../store/store";
import {
  DASHBOARD_CONSTANTS,
  DATE_FILTERS,
  DEVICE_TYPES,
  PAGES_URL,
  STATUS,
} from "../../../shared/constants/constants";
import {
  setDateSpanRedux,
  setFilters,
  setPagination,
  setSelectedSort,
  setSelectedTab,
  setStoreSearchText,
} from "../../../store/reducers/dashboard";
import { getAllOrders, getOrderPdf } from "../../../store/api/purchaseOrder";
import CustomSpinner from "../../../shared/components/customSpinner/customSpinner";
import { getOrderColor } from "../../../shared/utils/colorUtils";
import CustomBadge from "../../../shared/components/customBadge/customBadge";
import { FilterAlt, Search, UploadFile } from "@mui/icons-material";
import { uploadFileWrapper } from "../../../shared/utils/fileUtils";
import { useSnackbar } from "notistack";
import NavigateBackButton from "../../../shared/components/customNavigateButton/customNavigateButton";

const OrderTable = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const storeOrderState = useSelector((state: RootState) => state.order);
  const storePartyState = useSelector((state: RootState) => state.party);
  const storeStatusState = useSelector(
    (state: RootState) => state.dashboard.filters,
  );
  const storePageState = useSelector(
    (state: RootState) => state.dashboard.currentPage,
  );
  const storeRowsPerPageState = useSelector(
    (state: RootState) => state.dashboard.pageSize,
  );
  const storeSortState = {
    type: useSelector((state: RootState) => state.dashboard.sortField),
    value: useSelector((state: RootState) => state.dashboard.sortOrder),
  };
  const storeTabState = useSelector(
    (state: RootState) => state.dashboard.selectedTab,
  );
  const storeSearchTextState = useSelector(
    (state: RootState) => state.dashboard.searchText,
  );
  const storeDateState = useSelector(
    (state: RootState) => state.dashboard.dateSpan,
  );
  const theme = useTheme();
  const isLessThanMd = useMediaQuery(theme.breakpoints.down("md"));
  const isLessThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  // No status filter control is rendered on this page - default to Created,
  // matching what the mobile app should show per product decision.
  const [status, setStatus] = useState(DASHBOARD_CONSTANTS.STATUS_FILTER.ORDER.CREATED.id);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState<any>({});
  const [pageCount, setPageCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState(""); // Debounce state
  const tab = DASHBOARD_CONSTANTS.TABS.ORDERS;
  const [totalRows, setTotalRows] = useState(0);
  const statusList = DASHBOARD_CONSTANTS.STATUS_FILTER.ORDER;
  const [dateSpan, setDateSpan] = useState<any>({
    // Matches the current-month default now used on the GRN page - no date
    // filter control is rendered on this page either.
    type: DATE_FILTERS.MONTH.value,
    startDate: dayjs(new Date()),
    endDate: dayjs(new Date()),
  });
  const [sort, setSort] = useState<{
    type: string;
    value: "asc" | "desc";
  }>({
    type: "createdDate",
    value: "desc",
  });
  const [mount, setMount] = useState(false);
  const [isLoading, setIsLoading] = useState<string>("");
  const [openOrder, setOpenOrder] = useState<string>("");

  // Debounce side-effect hook
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500); // 500ms typing delay threshold

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  useEffect(() => {
    if (mount) {
      //updating redux states for preserving page and sorts
      dispatch(setPagination({ currentPage: page, pageSize: rowsPerPage }));
      dispatch(setFilters(status));
      dispatch(setSelectedTab(tab));
      dispatch(
        setSelectedSort({
          sortField: sort.type,
          sortOrder: sort.value === "asc" ? "asc" : "desc",
        }),
      );
      dispatch(setStoreSearchText(debouncedSearchText));
      dispatch(
        setDateSpanRedux({
          type: dateSpan.type,
          startDate: dateSpan.startDate.toString(),
          endDate: dateSpan.endDate.toString(),
        }),
      );
      fetchRequests(debouncedSearchText);
    }
  }, [
    page,
    rowsPerPage,
    status,
    tab,
    sort,
    statusList,
    debouncedSearchText, // Switch to debounced dependency
    dateSpan,
    storePartyState.currentOrg,
    mount,
  ]);

  useEffect(() => {
    // Set the initial local state from Redux values on mount
    if (storeTabState === DASHBOARD_CONSTANTS.TABS.ORDERS) {
      setStatus(storeStatusState || DASHBOARD_CONSTANTS.STATUS_FILTER.ORDER.CREATED.id);
      setPage(storePageState || 1);
      setRowsPerPage(storeRowsPerPageState || 10);
      setSort({
        type: storeSortState.type || "createdDate",
        value: storeSortState.value || "desc",
      });
      setSearchText(storeSearchTextState || "");
      setDebouncedSearchText(storeSearchTextState || "");
      setDateSpan({
        type: storeDateState.type || DATE_FILTERS.MONTH.value,
        startDate: dayjs(storeDateState.startDate) || dayjs(new Date()),
        endDate: dayjs(storeDateState.endDate) || dayjs(new Date()),
      });
    }
    setMount(true);
  }, []);

  const handleSearch = (searchString: string) => {
    setSearchText(searchString);
    setPage(1); // Reset page to 1 when a new search is initiated
  };

  const fetchRequests = (searchString: any) => {
    let inputSearchText = debouncedSearchText;
    if (searchString && searchString != "") {
      inputSearchText = searchString;
    }
    const { startDate, endDate } = getDates(dateSpan);
    store
      .dispatch(
        getAllOrders({
          status: status === "ORDERS_ALL" ? "ALL" : status,
          text: inputSearchText,
          page: page,
          pageSize: rowsPerPage,
          sort: sort.type + ":" + sort.value,
          org: storePartyState.currentOrg,
          startDate,
          endDate,
        }),
      )
      .unwrap()
      .then((resp: any) => {
        setData(resp.data);
        const totalRecords = resp.data?.totalRecords
          ? resp.data?.totalRecords
          : 0;
        setTotalRows(totalRecords);
        const pageCount = Math.ceil(totalRecords / rowsPerPage);
        setPageCount(pageCount);
      })
      .catch((err) => {
        enqueueSnackbar(`Error fetching order list: ${err}`, {
          variant: "error",
        });
      });
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleSort = (column: string) => {
    setPage(1);

    if (sort.type === column) {
      setSort({
        type: column,
        value: sort.value === "asc" ? "desc" : "asc",
      });
    } else {
      setSort({
        type: column,
        value: "asc",
      });
    }
  };

  const downloadOrderPdf = (id: string, orderCode: string) => {
    const reqBody = {
      orderId: id,
      orderUniqueCode: orderCode,
    };
    store
      .dispatch(getOrderPdf(reqBody))
      .unwrap()
      .catch(() => {
        enqueueSnackbar("Cannot generate Order PDF", { variant: "error" });
      });
  };

  const handleChangeDateType = (event: SelectChangeEvent) => {
    setPage(1);
    setDateSpan({
      ...dateSpan,
      type: event.target.value,
    });
  };

  const uploadDocument = async (file: any, type: string, order: any) => {
    try {
      setIsLoading(order?._id + "-" + "DocUpload");
      await uploadFileWrapper(file, type, order._id);
      enqueueSnackbar(
        `${file[0].name} Uploaded Successfully for Order: ${order?.orderCode}`,
        { variant: "success" },
      );
    } catch (error) {
      if (error instanceof AggregateError) {
        // Handle multiple errors
        console.error(`Aggregate Error caught: ${error.message}`);

        error.errors.forEach((subError) => {
          enqueueSnackbar(`${subError.message}`, { variant: "error" });
        });
      } else if (error instanceof Error) {
        // Handle standard errors (TypeError, ReferenceError, custom errors, etc.)
        console.log(error);
        enqueueSnackbar(`${error.message}`, { variant: "error" });
      }
    } finally {
      setIsLoading("");
    }
  };

  interface Column {
    id: string;
    label: string;
    width?: string;
    align?: "right" | "center" | "left";
    enableSort?: boolean;
    format?: (value: number) => string;
  }

  const orderItemColumns: Column[] = [
    {
      id: "_id",
      label: "Order ID",
      width: "12%",
      enableSort: true,
      align: "center",
    },
    {
      id: "finalAmount",
      label: "Order value",
      width: "18%",
      align: "center",
    },
    {
      id: "statusId",
      label: "Status",
      width: "21%",
      align: "center",
    },
    {
      id: "createdDate",
      label: "Created date",
      width: "15%",
      enableSort: true,
      align: "center",
    },
    {
      id: "expectedDeliveryDate",
      label: "Due date",
      width: "15%",
      enableSort: true,
      align: "center",
    },
    {
      id: "actionsId",
      label: "Actions",
      width: "19%",
      enableSort: false,
      align: "center",
    },
  ];

  const orderTableRows = useMemo(() => {
    switch (storeOrderState.loading) {
      case true:
      case undefined:
        return (
          <TableRow sx={{ height: "80%" }}>
            <TableCell
              align="center"
              sx={{ borderBottom: "none" }}
              colSpan={orderItemColumns.length}
            >
              <CustomSpinner size={"2rem"} />
            </TableCell>
          </TableRow>
        );
      case false:
        return data?.orders?.length ? (
          data.orders.map((order: any) => {
            let statusId = order?.statusId;
            if (order && order.paymentStatus) {
              if (order.paymentStatus === STATUS.PAYMENT_STATUS.REJECTED) {
                statusId = "PAYMENT_REJECTED";
              }
            } else if (order && order.threeWayMatchResult) {
              if (
                order.threeWayMatchResult ===
                STATUS.THREE_WAY_MATCH_STATUS.REJECTED
              ) {
                statusId = STATUS.ORDER.THREE_WAY_MATCH_REJECTED.id;
              }
            }
            const className = getOrderColor(
              STATUS.ORDER[statusId as keyof typeof STATUS.ORDER].name,
            );
            if (isLessThanMd) {
              return (
                <Stack width={"100%"} key={order?._id}>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    onClick={() => {
                      openOrder === order?._id
                        ? setOpenOrder("")
                        : setOpenOrder(order?._id);
                    }}
                  >
                    <Stack direction={"row"} spacing={1} alignItems={"center"}>
                      <Typography>
                        {order?.referenceNumber && order?.referenceNumber !== ""
                          ? order?.referenceNumber
                          : ((order?.orderCode
                              ? order?.orderCode.split("/")[5]
                              : order?._id) ?? "-")}
                      </Typography>
                      <CustomBadge
                        content={
                          STATUS.ORDER[statusId as keyof typeof STATUS.ORDER].name
                        }
                        className={className}
                        sx={{ minWidth: "unset" }}
                      />
                    </Stack>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      {isLoading === order?._id + "-" + "DocUpload" ? (
                        <CustomSpinner />
                      ) : (
                        <IconButton
                          sx={{ borderRadius: 0, mr: 1 }}
                          size="large"
                        >
                          <UploadFile height={"20px"} width={"20px"} />
                          <Input
                            type="file"
                            id="signedFileUpload"
                            onChange={(event) => {
                              if (event?.target instanceof HTMLInputElement) {
                                uploadDocument(
                                  event?.target?.files,
                                  "signedOrder",
                                  order,
                                );
                              }
                            }}
                            sx={{
                              width: 1,
                              height: 1,
                              opacity: 0,
                              border: "none",
                              position: "absolute",
                              top: "0",
                              left: "0",
                              right: "0",
                              bottom: "0",
                              cursor: "pointer",
                            }}
                          />
                        </IconButton>
                      )}
                      <IconButton
                        sx={{ borderRadius: 0, mr: 1 }}
                        onClick={() =>
                          downloadOrderPdf(order._id, order.orderCode)
                        }
                        size="large"
                      >
                        <DownloadIcon height={"20px"} width={"20px"} />
                      </IconButton>
                    </Stack>
                  </Stack>
                  <Collapse in={openOrder === order?._id}>
                    <Stack direction={"row"} spacing={1}>
                      <Typography>Status: </Typography>
                      <Typography>
                        {
                          STATUS.ORDER[statusId as keyof typeof STATUS.ORDER]
                            .name
                        }
                      </Typography>
                    </Stack>
                    <Stack direction={"row"} spacing={1}>
                      <Typography>Created Date: </Typography>
                      <Typography>
                        {dayjs(order.referenceDate || order.createdDate).format(
                          "DD/MM/YYYY",
                        )}
                      </Typography>
                    </Stack>
                    <Stack direction={"row"} spacing={1}>
                      <Typography>Expected Delivery Date: </Typography>
                      <Typography>
                        {dayjs(order.expectedDeliveryDate).format("DD/MM/YYYY")}
                      </Typography>
                    </Stack>
                  </Collapse>
                </Stack>
              );
            } else {
              return (
                <TableRow key={order._id}>
                  <TableCell align={orderItemColumns[0].align}>
                    <Typography sx={{ wordBreak: "break-all" }}>
                      {order?.referenceNumber && order?.referenceNumber !== ""
                        ? order?.referenceNumber
                        : ((order?.orderCode
                            ? order?.orderCode.split("/")[5]
                            : order?._id) ?? "-")}
                    </Typography>
                  </TableCell>
                  <TableCell align={orderItemColumns[1].align}>
                    <Typography sx={{ wordBreak: "break-all" }}>
                      {order.grandTotal != null &&
                      !isNaN(order?.grandTotal) &&
                      order?.grandTotal !== "" &&
                      typeof order?.grandTotal !== "string"
                        ? order.grandTotal.toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                            style: "currency",
                            currency: "INR",
                          })
                        : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align={orderItemColumns[2].align}>
                    <CustomBadge
                      className={className}
                      content={
                        STATUS.ORDER[statusId as keyof typeof STATUS.ORDER].name
                      }
                    />
                  </TableCell>

                  <TableCell align={orderItemColumns[3].align}>
                    <Typography sx={{ wordBreak: "break-all" }}>
                      {dayjs(order.referenceDate || order.createdDate).format(
                        "DD/MM/YYYY",
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align={orderItemColumns[4].align}>
                    <Typography sx={{ wordBreak: "break-all" }}>
                      {dayjs(order.expectedDeliveryDate).format("DD/MM/YYYY")}
                    </Typography>
                  </TableCell>
                  <TableCell align={orderItemColumns[5].align}>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      {isLoading === order?._id + "-" + "DocUpload" ? (
                        <CustomSpinner />
                      ) : (
                        <IconButton
                          sx={{ borderRadius: 0, mr: 1 }}
                          size="large"
                        >
                          <UploadFile height={"20px"} width={"20px"} />
                          <Input
                            type="file"
                            id="signedFileUpload"
                            onChange={(event) => {
                              if (event?.target instanceof HTMLInputElement) {
                                uploadDocument(
                                  event?.target?.files,
                                  "signedOrder",
                                  order,
                                );
                              }
                            }}
                            sx={{
                              width: 1,
                              height: 1,
                              opacity: 0,
                              border: "none",
                              position: "absolute",
                              top: "0",
                              left: "0",
                              right: "0",
                              bottom: "0",
                              cursor: "pointer",
                            }}
                          />
                        </IconButton>
                      )}
                      <IconButton
                        sx={{ borderRadius: 0, mr: 1 }}
                        onClick={() =>
                          downloadOrderPdf(order._id, order.orderCode)
                        }
                        size="large"
                      >
                        <DownloadIcon height={"20px"} width={"20px"} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            }
          })
        ) : (
          <TableRow>
            <TableCell
              sx={{ borderBottom: "none" }}
              colSpan={orderItemColumns.length}
            >
              No Data
            </TableCell>
          </TableRow>
        );
    }
  }, [
    orderItemColumns.length,
    data,
    storeOrderState.loading,
    isLoading,
    isLessThanMd,
    openOrder,
  ]);

  return (
    <Stack width={"100%"} p={2} boxSizing="border-box">
      <NavigateBackButton
        label={"Back to Home"}
        navigateLink={PAGES_URL.HOME}
      />
      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        justifyContent="space-between"
        alignItems={{
          sm: "flex-start",
          xs: "center",
        }}
        spacing={2}
        my={1}
        >
        <Typography fontSize={26} fontWeight={600}>
          Orders List
        </Typography>
        <FormControl sx={{ m: 1 }} variant="standard"  fullWidth={isLessThanSm}>
          <InputLabel htmlFor={`search-input`}>Search</InputLabel>
          <Input
            id={`search-input`}
            value={searchText} // Controlled input to ensure text syncs on initial mount
            startAdornment={
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            }
            onChange={(e) => handleSearch(e.target.value)}
          />
        </FormControl>
      </Stack>
      {!isLessThanMd && (
        <TableContainer>
          <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                {orderItemColumns.map((orderItemColumn) => (
                  <TableCell
                    align={orderItemColumn.align}
                    key={orderItemColumn.id}
                    sx={{ width: orderItemColumn.width }}
                  >
                    {orderItemColumn.enableSort && (
                      <TableSortLabel
                        className={
                          sort.type === orderItemColumn.id &&
                          sort.value === "desc"
                            ? "sortColumn inverted"
                            : "sortColumn"
                        }
                        active={true}
                        direction={sort.value}
                        hideSortIcon={true}
                        onClick={() => handleSort(orderItemColumn.id)}
                        IconComponent={() => {
                          if (sort.type !== orderItemColumn.id) {
                            return <SortableColumn />;
                          } else {
                            return <SortIcon />;
                          }
                        }}
                      >
                        <p>{orderItemColumn.label}</p>
                      </TableSortLabel>
                    )}

                    {!orderItemColumn.enableSort && orderItemColumn.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{orderTableRows}</TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={orderItemColumns.length}>
                  <Stack
                    direction={"row"}
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <Typography>
                      Showing data{" "}
                      {totalRows ? (page - 1) * rowsPerPage + 1 : totalRows} to{" "}
                      {(page - 1) * rowsPerPage + rowsPerPage < totalRows
                        ? (page - 1) * rowsPerPage + rowsPerPage
                        : totalRows}{" "}
                      of {totalRows} entries
                    </Typography>
                    <Stack
                      direction={"row"}
                      spacing={1}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      <Pagination
                        count={pageCount}
                        page={page}
                        onChange={handleChangePage}
                        dir={""}
                      />
                      <Typography>Result Per Page</Typography>
                      <Select
                        labelId="row_count"
                        size="small"
                        id="RowCountField"
                        sx={{
                          ".MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          paddingRight: "0px",
                        }}
                        value={rowsPerPage}
                        onChange={(e) => setRowsPerPage(Number(e.target.value))}
                      >
                        {DASHBOARD_CONSTANTS.PAGE_SIZES.map((key) => (
                          <MenuItem key={key} value={key}>
                            {key}
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
      {isLessThanMd && (
        <Stack spacing={1} divider={<Divider variant="fullWidth" />}>
          {orderTableRows}
          <Pagination
            count={pageCount}
            page={page}
            onChange={handleChangePage}
            dir={""}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default OrderTable;
